import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Chip, Grid, IconButton, Modal } from "@mui/material";
import { Table, Tag } from "antd";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { FiEdit } from "react-icons/fi";
import { FaBan, FaCheckCircle } from "react-icons/fa";
import { useSnackbar } from "notistack";
import NunitoText from "../components/general/NunitoText";
import ReusableButton from "../components/general/ReusableButton";
import LoadingBackdrop from "../components/general/LoadingBackdrop";
import Loader from "../components/general/Loader";
import NoData from "../components/general/NoData";
import LightTooltip from "../components/general/LightTooltip";
import AddAssigneeModal from "../components/AssignTranslation/AddAssigneeModal";
import EditAssigneeModal from "../components/AssignTranslation/EditAssigneeModal";
import ConfirmModal from "../components/modal/ConfirmModal";
import { getAssignments } from "../functions/getData";
import { toggleAssignmentActive } from "../functions/patchData";
import { showNotification } from "../functions/snackbar";
import { isAdmin } from "../functions/checkrole";
import { gradeOptions } from "../functions/general";

const chipStyle = {
  backgroundColor: "#F16774",
  color: "#FFF",
  marginRight: 4,
  marginBottom: 4,
};

// Same header look as the Print Certificate table
const headerTitle = (text) => (
  <span
    style={{
      fontSize: 20,
      fontWeight: "bold",
      color: "#144A94",
      whiteSpace: "nowrap",
    }}
  >
    {text}
  </span>
);

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
};

const pickName = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value !== "object") return null;
  return (
    value.display_name ||
    value.name ||
    value.option ||
    value.label ||
    value.title ||
    null
  );
};

/** Resolve a language id using global options (filled when Redux loads). */
const labelFromLanguageId = (id, idMap) => {
  if (id == null || id === "") return null;
  return idMap.get(Number(id)) || idMap.get(String(id)) || null;
};

/**
 * One language row from the assignments index API (pivot, nested `language`, or bare id).
 */
const pickLanguageDisplay = (lang, idMap) => {
  if (lang == null) return null;
  if (typeof lang === "string" || typeof lang === "number") {
    const resolved = labelFromLanguageId(lang, idMap);
    return resolved || String(lang);
  }
  if (typeof lang !== "object") return null;
  if (lang.language && typeof lang.language === "object") {
    const nested = pickName(lang.language);
    if (nested) return nested;
    const lid = lang.language?.id;
    const fromMap = labelFromLanguageId(lid, idMap);
    if (fromMap) return fromMap;
  }
  const direct =
    pickName(lang) ||
    lang.locale ||
    lang.code ||
    lang.language_name ||
    lang.short_name ||
    lang.lang;
  if (direct) return String(direct);
  const lid = lang.language_id ?? lang.language?.id ?? lang.id;
  if (lid != null && lid !== "") {
    const fromMap = labelFromLanguageId(lid, idMap);
    if (fromMap) return fromMap;
    return `Language (${lid})`;
  }
  return null;
};

/** Collect language rows from several possible index payload shapes. */
const collectLanguageEntries = (raw) => {
  const keys = [
    "languages",
    "language",
    "assigned_languages",
    "assignment_languages",
    "translation_languages",
    "competition_languages",
  ];
  for (const k of keys) {
    const v = raw[k];
    if (v == null) continue;
    if (Array.isArray(v)) return v;
    if (typeof v === "object") return Object.values(v);
  }
  if (Array.isArray(raw.language_ids))
    return raw.language_ids.map((id) => ({ language_id: id }));
  if (raw.language_id != null && raw.language_id !== "")
    return [{ language_id: raw.language_id }];
  return [];
};

const extractLanguageLabels = (raw, idMap) =>
  collectLanguageEntries(raw)
    .map((e) => pickLanguageDisplay(e, idMap))
    .filter(Boolean);

const pickGrade = (grade) => {
  if (grade === null || grade === undefined) return null;
  if (typeof grade === "number" || typeof grade === "string") {
    const opt = gradeOptions.find((x) => String(x.id) === String(grade));
    return opt ? opt.name : String(grade);
  }
  if (typeof grade === "object") {
    const name = pickName(grade);
    if (name) return name;
    const id = grade.id ?? grade.grade_id ?? grade.grade;
    const opt = gradeOptions.find((x) => String(x.id) === String(id));
    return opt ? opt.name : id !== undefined ? String(id) : null;
  }
  return null;
};

const pickUser = (user) => {
  if (user === null || user === undefined) return null;
  if (typeof user === "string" || typeof user === "number") return String(user);
  if (typeof user !== "object") return null;
  return (
    pickName(user) ||
    user.username ||
    pickName(user.user) ||
    user.user?.username ||
    null
  );
};

const joinDashList = (arr) => {
  if (!Array.isArray(arr) || !arr.length) return "-";
  const clean = arr.filter(Boolean);
  return clean.length ? clean.join(" - ") : "-";
};

/** First two language labels inline; if more, +N with LightTooltip listing all (app pattern). */
const renderLanguageCells = (row) => {
  const langs = (row.languages || []).filter(Boolean);
  if (!langs.length) return "-";
  if (langs.length <= 2) {
    return langs.map((l, i) => (
      <Chip
        key={`${row.id}-lang-${i}`}
        label={l}
        size="small"
        style={chipStyle}
      />
    ));
  }
  const rest = langs.slice(2);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "6px",
      }}
    >
      {langs.slice(0, 2).map((l, i) => (
        <Chip
          key={`${row.id}-lang-${i}`}
          label={l}
          size="small"
          style={chipStyle}
        />
      ))}
      <LightTooltip
        title={
          <Box sx={{ py: 0.5, px: 1, maxWidth: 320 }}>
            {langs.map((l, i) => (
              <div
                key={`${row.id}-lang-all-${i}`}
                style={{
                  fontFamily: "Nunito",
                  fontSize: 14,
                  color: "#000",
                  padding: "2px 0",
                }}
              >
                {l}
              </div>
            ))}
          </Box>
        }
        rollover={1}
        topic={0}
        hide={0}
        fixPosition
      >
        <Chip
          label={`+${rest.length}`}
          size="small"
          sx={{
            cursor: "pointer",
            backgroundColor: "#144A94",
            color: "#FFF",
            fontFamily: "Nunito",
            fontWeight: 600,
          }}
        />
      </LightTooltip>
    </Box>
  );
};

const normalizeRow = (raw) => {
  // Backend response shape isn't strictly defined yet; this gracefully accepts a few common shapes.
  const orgNames = Array.isArray(raw.organizations)
    ? raw.organizations.map(pickName).filter(Boolean).join(", ")
    : "";
  return {
    id: raw.id,
    competition:
      raw.competition_name || pickName(raw.competition) || "-",
    country: raw.country_name || pickName(raw.country) || "-",
    organization:
      raw.organization_name ||
      pickName(raw.organization) ||
      orgNames ||
      "-",
    languages: extractLanguageLabels(raw, new Map()),
    users: Array.isArray(raw.users)
      ? raw.users.map(pickUser).filter(Boolean)
      : [],
    grades: Array.isArray(raw.grades)
      ? raw.grades.map(pickGrade).filter(Boolean)
      : [],
    from_date: raw.from_date || raw.from || null,
    to_date: raw.to_date || raw.to || null,
    is_active:
      raw.is_active !== undefined
        ? Boolean(raw.is_active)
        : raw.status === "active",
    raw,
  };
};

export default function AssignTranslation() {
  document.title = "Assign Translation";

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [loadingTable, setLoadingTable] = useState(false);
  const [firstLoaded, setFirstLoaded] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [onAdd, setOnAdd] = useState(false);
  const [onEdit, setOnEdit] = useState(false);
  const [onToggle, setOnToggle] = useState(false);
  const [editId, setEditId] = useState(null);
  const [toggleRow, setToggleRow] = useState(null);

  const languageOptions = useSelector((state) => state.languageOptions || []);
  const languageLabelById = useMemo(() => {
    const m = new Map();
    for (const l of languageOptions) {
      if (l == null || l.id == null) continue;
      const label = l.name || l.display_name || String(l.id);
      m.set(Number(l.id), label);
      m.set(String(l.id), label);
    }
    return m;
  }, [languageOptions]);

  const tableRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        languages: extractLanguageLabels(row.raw, languageLabelById),
      })),
    [rows, languageLabelById]
  );

  const controller = useMemo(() => new AbortController(), []);
  const signal = controller.signal;

  const fetchAssignments = (sig) => {
    setLoadingTable(true);
    getAssignments("", sig)
      .then((res) => {
        const body = res?.data;
        const list = Array.isArray(body)
          ? body
          : Array.isArray(body?.data)
          ? body.data
          : Array.isArray(res)
          ? res
          : [];
        setRows(list.map((raw) => normalizeRow(raw)));
      })
      .catch(() => {})
      .finally(() => {
        setLoadingTable(false);
        setFirstLoaded(true);
      });
  };

  useEffect(() => {
    fetchAssignments(signal);
    return () => controller.abort();
  }, [controller, signal]);

  const afterAdd = (cancel, data) => {
    if (data?.status === 200 || data?.status === 201) {
      cancel();
      showNotification(
        "success",
        data?.message || "Assignment created",
        enqueueSnackbar,
        closeSnackbar
      );
      fetchAssignments(signal);
    } else if (data?.anySucceeded) {
      // Some organizations were assigned before a later one failed.
      fetchAssignments(signal);
    }
  };

  const afterEdit = (cancel, data) => {
    if (data?.status === 200 || data?.status === 201) {
      cancel();
      showNotification(
        "success",
        data?.message || "Assignment updated",
        enqueueSnackbar,
        closeSnackbar
      );
      fetchAssignments(signal);
    }
  };

  const cancelEdit = () => {
    setOnEdit(false);
    setEditId(null);
  };

  const cancelToggle = () => {
    setOnToggle(false);
    setToggleRow(null);
  };

  const confirmToggle = () => {
    if (!toggleRow) return;
    setActionLoading(true);
    toggleAssignmentActive(toggleRow.id)
      .then((d) => {
        if ([200, 201].includes(Number(d?.status)) || d?.message) {
          showNotification(
            "success",
            d?.message ||
              `Assignment ${
                toggleRow.is_active ? "disabled" : "enabled"
              } successfully`,
            enqueueSnackbar,
            closeSnackbar
          );
          fetchAssignments(signal);
        }
      })
      .catch(() => {})
      .finally(() => {
        setActionLoading(false);
        cancelToggle();
      });
  };

  const columns = [
    {
      title: headerTitle("Competition"),
      dataIndex: "competition",
      key: "competition",
      // Keep competition names on a single line; the column grows to fit
      onCell: () => ({ style: { whiteSpace: "nowrap" } }),
      render: (competition, row) => (
        <Box>
          <div>{competition}</div>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "4px",
              mt: 0.5,
              ml: "-8px",
            }}
          >
            <LightTooltip title="Edit" rollover={0} topic={0} hide={0}>
              <IconButton
                size="small"
                onClick={() => {
                  setEditId(row.id);
                  setOnEdit(true);
                }}
                style={{ color: "#000" }}
              >
                <FiEdit />
              </IconButton>
            </LightTooltip>
            <LightTooltip
              title={row.is_active ? "Disable" : "Enable"}
              rollover={0}
              topic={0}
              hide={0}
            >
              <IconButton
                size="small"
                onClick={() => {
                  setToggleRow(row);
                  setOnToggle(true);
                }}
                style={{ color: row.is_active ? "#E83042" : "#3EC49D" }}
              >
                {row.is_active ? <FaBan /> : <FaCheckCircle />}
              </IconButton>
            </LightTooltip>
          </Box>
        </Box>
      ),
    },
    {
      title: headerTitle("Country"),
      dataIndex: "country",
      key: "country",
    },
    {
      title: headerTitle("Languages"),
      key: "languages",
      render: (_, row) => renderLanguageCells(row),
    },
    {
      title: headerTitle("Organization"),
      dataIndex: "organization",
      key: "organization",
    },
    {
      title: headerTitle("Users"),
      key: "users",
      render: (_, row) => joinDashList(row.users),
    },
    {
      title: headerTitle("Grades"),
      key: "grades",
      render: (_, row) => joinDashList(row.grades),
    },
    {
      title: headerTitle("From"),
      key: "from_date",
      render: (_, row) => formatDate(row.from_date),
    },
    {
      title: headerTitle("To"),
      key: "to_date",
      render: (_, row) => formatDate(row.to_date),
    },
    {
      title: headerTitle("Status"),
      key: "status",
      render: (_, row) => (
        <Tag color={row.is_active ? "green" : "red"}>
          {row.is_active ? "Active" : "Disabled"}
        </Tag>
      ),
    },
  ];

  return (
    <Box className="wrapperBox">
      <LoadingBackdrop loading={actionLoading} />
      <Grid
        className="firstRowContainer"
        container
        justifyContent="space-between"
      >
        <div className="dashboardAndSelfBtnDiv">
          <ReusableButton
            text="Dashboard"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            width={125}
            br={18}
            to="/dashboard"
            iconType="home"
          />
          <ChevronRightIcon />
          <ReusableButton
            text="Assign Translation"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            br={18}
          />
        </div>
      </Grid>

      <Grid className="tableContainer" container>
        <NunitoText
          value="Assign Translation"
          fontSize={40}
          fontWeight={700}
          italic
          color="#144A94"
        />
        <Grid
          className="searchAndBtnContainer"
          container
          alignItems="center"
          justifyContent="flex-end"
        >
          {isAdmin() && (
            <div className="endBtnDiv">
              <ReusableButton
                text="Add Assignee"
                fontSize={15}
                bgColor="#5E75C3"
                height={46}
                iconType="add"
                onClick={() => setOnAdd(true)}
              />
            </div>
          )}
        </Grid>

        {loadingTable && <Loader height={600} />}

        {!loadingTable && firstLoaded && rows.length === 0 && (
          <NoData height={600} />
        )}

        {!loadingTable && rows.length > 0 && (
          <Table
            columns={columns}
            dataSource={tableRows}
            rowKey="id"
            pagination={false}
            scroll={{ x: true }}
            style={{ width: "100%", marginTop: 24 }}
          />
        )}
      </Grid>

      <Modal open={onAdd} onClose={() => setOnAdd(false)}>
        <>
          {onAdd ? (
            <AddAssigneeModal
              open={onAdd}
              setLoading={setActionLoading}
              setOnAdd={setOnAdd}
              afterAdd={afterAdd}
            />
          ) : null}
        </>
      </Modal>

      <Modal open={onEdit} onClose={cancelEdit}>
        <>
          {editId && (
            <EditAssigneeModal
              id={editId}
              row={tableRows.find((r) => r.id === editId)}
              setLoading={setActionLoading}
              afterEdit={afterEdit}
              cancelEdit={cancelEdit}
            />
          )}
        </>
      </Modal>

      <Modal open={onToggle} onClose={cancelToggle}>
        <>
          {toggleRow && (
            <ConfirmModal
              action={`${toggleRow.is_active ? "disable" : "enable"} this assignment?`}
              itemName={`${toggleRow.competition} - ${toggleRow.country}`}
              confirmFunction={confirmToggle}
              cancelConfirm={cancelToggle}
            />
          )}
        </>
      </Modal>
    </Box>
  );
}
